/**
 * Custom Super Search Engine - Warehouse Theme
 * Handles fuzzy matching, did-you-mean typo corrections, recent searches,
 * trending collections, keyboard navigation, and responsive rendering.
 */

(function () {
  // --- GLOBALS & CONFIGURATION ---
  let searchInput = null;
  let searchForm = null;
  let customContainer = null;
  let translations = {};
  let dictionary = new Set();
  let popularCollections = [];
  let debounceTimeout = null;
  let currentFocusableItems = [];
  let activeFocusIndex = -1;
  // Búsqueda: cancelación de peticiones en vuelo, guardia anti-obsoletos y caché por consulta.
  let currentSearchController = null;
  let latestSearchToken = 0;
  const searchCache = new Map();

  // Normaliza texto para comparar sin acentos ni mayúsculas (es-ES: "manilla"/"manílla").
  function normalizeText(s) {
    return (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  // --- KEYBOARD CONFIG ---
  const FOCUSABLE_SELECTOR = '.custom-search-pill-btn, .custom-search-suggest-link, .custom-search-product-card, .custom-search-typo-link, .custom-search-view-all-btn';

  // --- INITIALIZATION ---
  document.addEventListener('DOMContentLoaded', () => {
    initSearch();
  });

  // Safe fallback if DOMContentLoaded already fired
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initSearch();
  }

  function initSearch() {
    // Avoid double initialization
    if (window.customSearchInitialized) return;
    window.customSearchInitialized = true;

    searchInput = document.querySelector('.search-bar__input');
    searchForm = document.querySelector('form.search-bar');
    customContainer = document.getElementById('custom-search-container');

    const transScript = document.getElementById('custom-search-translations');
    if (transScript) {
      try {
        translations = JSON.parse(transScript.textContent);
      } catch (e) {
        console.error('Error parsing search translations', e);
      }
    }

    if (!searchInput || !customContainer) {
      console.warn('Custom search input or container not found in DOM.');
      return;
    }

    // Build dictionary & popular categories for spellcheck/suggestions
    buildVocabulary();

    // Attach Event Listeners
    setupEventListeners();
  }

  // --- VOCABULARY BUILDER (Fuzzy Match / Typo Correction) ---
  async function buildVocabulary() {
    try {
      // 1. Fetch collections for trending/popular lists and spellcheck
      const collectionsRes = await fetch('/collections.json?limit=30');
      if (collectionsRes.ok) {
        const data = await collectionsRes.json();
        if (data.collections) {
          // Map known English collection titles to Spanish
          const collectionTitleMap = {
            'best selling products': 'Más vendidos',
            'best sellers': 'Más vendidos',
            'best selling': 'Más vendidos',
            'new arrivals': 'Novedades',
            'all products': 'Todos los productos',
            'all': 'Todos',
            'featured': 'Destacados',
            'sale': 'Ofertas'
          };

          popularCollections = data.collections.map(c => {
            const mapped = collectionTitleMap[c.title.toLowerCase().trim()];
            return {
              title: mapped || c.title,
              url: `/collections/${c.handle}`
            };
          });

          // Add collections to dictionary
          popularCollections.forEach(c => addWordsToDictionary(c.title));
        }
      }

      // 2. Fetch products to index titles, types, and vendors
      const productsRes = await fetch('/products.json?limit=80');
      if (productsRes.ok) {
        const data = await productsRes.json();
        if (data.products) {
          data.products.forEach(p => {
            addWordsToDictionary(p.title);
            if (p.product_type) addWordsToDictionary(p.product_type);
            if (p.vendor) addWordsToDictionary(p.vendor);
          });
        }
      }
    } catch (e) {
      console.error('Error building search vocabulary', e);
    }
  }

  function addWordsToDictionary(text) {
    if (!text) return;
    // Clean text: normaliza (sin acentos), quita caracteres especiales
    const words = normalizeText(text)
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"']/g, ' ')
      .split(/\s+/);

    words.forEach(word => {
      // Only index meaningful words (length >= 3 characters)
      if (word.length >= 3 && !isStopWord(word)) {
        dictionary.add(word);
      }
    });
  }

  function isStopWord(word) {
    const stops = ['con', 'para', 'del', 'los', 'las', 'una', 'uno', 'unas', 'unos', 'este', 'esta', 'como', 'todo'];
    return stops.includes(word);
  }

  // --- LEVENSHTEIN FUZZY MATCH LOGIC ---
  function getLevenshteinDistance(a, b) {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    const matrix = [];
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }
    return matrix[b.length][a.length];
  }

  function suggestCorrections(query) {
    if (!query) return null;
    const words = normalizeText(query).split(/\s+/);
    let corrected = false;
    const correctedWords = words.map(word => {
      // Skip very short words, numbers, or exact matches
      if (word.length <= 3 || /^\d+$/.test(word) || dictionary.has(word)) {
        return word;
      }

      let bestMatch = word;
      let minDistance = 3; // Max tolerance: edit distance <= 2

      for (const dictWord of dictionary) {
        // Skip comparing if length difference is too large to save CPU
        if (Math.abs(dictWord.length - word.length) > 2) continue;

        const dist = getLevenshteinDistance(word, dictWord);
        if (dist < minDistance) {
          minDistance = dist;
          bestMatch = dictWord;
        }
      }

      if (bestMatch !== word) {
        corrected = true;
        return bestMatch;
      }
      return word;
    });

    return corrected ? correctedWords.join(' ') : null;
  }

  // --- RECENT SEARCHES MANAGER ---
  function getRecentSearches() {
    try {
      const stored = localStorage.getItem('custom_recent_searches');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecentSearch(term) {
    if (!term || term.trim() === '') return;
    const cleanTerm = term.trim();
    let recent = getRecentSearches();

    // Remove duplicates and put current term at first position
    recent = recent.filter(t => t.toLowerCase() !== cleanTerm.toLowerCase());
    recent.unshift(cleanTerm);

    // Limit to 5 entries
    recent = recent.slice(0, 5);

    try {
      localStorage.setItem('custom_recent_searches', JSON.stringify(recent));
    } catch (e) {
      console.error('Failed to save recent search to localStorage', e);
    }
  }

  function removeRecentSearch(term) {
    let recent = getRecentSearches();
    recent = recent.filter(t => t !== term);
    try {
      localStorage.setItem('custom_recent_searches', JSON.stringify(recent));
    } catch (e) {
      console.error(e);
    }
  }

  function clearAllRecentSearches() {
    try {
      localStorage.removeItem('custom_recent_searches');
    } catch (e) {
      console.error(e);
    }
  }

  // --- SEARCH RENDERING ---
  function setupEventListeners() {
    // 1. Listen to typing with debounce
    searchInput.addEventListener('input', () => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        handleSearchChange();
      }, 200);
    });

    // 2. Listen to input focus
    searchInput.addEventListener('focus', () => {
      handleSearchChange();
    });

    // 3. Close search when clicking outside (use mousedown to avoid race with focus)
    document.addEventListener('mousedown', (e) => {
      // If click is inside the search input or the custom container, do nothing
      if (searchInput.contains(e.target) || customContainer.contains(e.target)) return;
      // Also ignore clicks on the search form wrapper (buttons, etc.)
      const searchBarEl = searchInput.closest('.search-bar');
      if (searchBarEl && searchBarEl.contains(e.target)) return;
      hideDropdown();
    });

    // Prevent search input from blurring when clicking items inside the dropdown
    customContainer.addEventListener('mousedown', (e) => {
      // Only prevent default on clicks that are not direct text fields if we had any
      e.preventDefault();
    });

    // 4. Keyboard Navigation inside input
    searchInput.addEventListener('keydown', handleKeyboardNav);

    // 5. Save recent search on form submit
    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        saveRecentSearch(searchInput.value);
      });
    }

    // 6. Handle click events inside custom search dropdown (delegation)
    customContainer.addEventListener('click', (e) => {
      e.stopPropagation();
      const pillBtn = e.target.closest('.custom-search-pill-btn');
      const clearBtn = e.target.closest('.custom-search-pill-btn-clear');
      const typoLink = e.target.closest('.custom-search-typo-link');
      const productCard = e.target.closest('.custom-search-product-card');
      const suggestLink = e.target.closest('.custom-search-suggest-link');

      if (clearBtn) {
        e.preventDefault();
        clearAllRecentSearches();
        renderEmptyOrRecent();
      } else if (pillBtn) {
        e.preventDefault();
        const term = pillBtn.getAttribute('data-term');
        const isDelete = e.target.closest('span.delete-pill-x');
        if (isDelete) {
          removeRecentSearch(term);
          renderEmptyOrRecent();
        } else {
          searchInput.value = term;
          saveRecentSearch(term);
          handleSearchChange();
          searchInput.focus();
        }
      } else if (typoLink) {
        e.preventDefault();
        const term = typoLink.getAttribute('data-term');
        searchInput.value = term;
        saveRecentSearch(term);
        handleSearchChange();
        searchInput.focus();
      } else if (productCard) {
        // Save product name to recent searches just before navigating
        const title = productCard.querySelector('.custom-search-product-title').innerText;
        saveRecentSearch(title);
      } else if (suggestLink) {
        const term = suggestLink.getAttribute('data-term');
        if (term) {
          saveRecentSearch(term);
        }
      }
    });
  }

  function handleSearchChange() {
    const val = searchInput.value.trim();
    if (val === '') {
      renderEmptyOrRecent();
    } else {
      performSearch(val);
    }
  }

  function hideDropdown() {
    customContainer.style.display = 'none';
    const searchBar = searchInput.closest('.search-bar');
    if (searchBar) {
      searchBar.classList.remove('is-expanded');
    }
    resetFocusState();
  }

  function showDropdown() {
    customContainer.style.display = 'block';
    const searchBar = searchInput.closest('.search-bar');
    if (searchBar) {
      searchBar.classList.add('is-expanded');
    }
    // Make sure we have responsive classes locked if expanded
    const wrapper = searchInput.closest('.header__search-bar-wrapper');
    if (wrapper) {
      wrapper.classList.add('is-fixed');
    }
  }

  // --- RENDER RECENT AND POPULAR PANEL ---
  function renderEmptyOrRecent() {
    showDropdown();
    const recent = getRecentSearches();
    let leftHTML = '';

    // Render Recent Searches
    if (recent.length > 0) {
      leftHTML += `
        <div class="custom-search-section">
          <div class="custom-search-flex-header">
            <h4 class="custom-search-title">${translations.recent_searches}</h4>
            <button class="custom-search-pill-btn-clear" type="button">${translations.clear}</button>
          </div>
          <div class="custom-search-pills-list">
            ${recent.map(term => `
              <button class="custom-search-pill-btn" type="button" data-term="${escapeHtml(term)}">
                <span class="custom-search-pill-text">${escapeHtml(term)}</span>
                <span class="delete-pill-x" style="font-weight:700; margin-left:6px; opacity:0.6;">&times;</span>
              </button>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Render Popular Collections
    if (popularCollections.length > 0) {
      leftHTML += `
        <div class="custom-search-section">
          <h4 class="custom-search-title">${translations.popular_categories}</h4>
          <div class="custom-search-pills-list">
            ${popularCollections.slice(0, 8).map(c => `
              <a href="${c.url}" class="custom-search-pill-btn" data-term="${escapeHtml(c.title)}">
                ${escapeHtml(c.title)}
              </a>
            `).join('')}
          </div>
        </div>
      `;
    } else {
      // Hardcoded fallback categories if no collections fetched yet
      const fallbackCategories = ['Cerraduras', 'Manillas', 'Herrajes', 'Pomos', 'Bisagras'];
      leftHTML += `
        <div class="custom-search-section">
          <h4 class="custom-search-title">${translations.popular_categories}</h4>
          <div class="custom-search-pills-list">
            ${fallbackCategories.map(c => `
              <button class="custom-search-pill-btn" type="button" data-term="${c}">
                ${c}
              </button>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Right pane: show skeleton while loading products
    const skeletonRight = `
      <div class="custom-search-section">
        <h4 class="custom-search-title">Más vendidos</h4>
        <div class="custom-search-product-grid">
          ${Array(6).fill(0).map(() => `
            <div class="custom-search-skeleton-card">
              <div class="custom-search-skeleton-img"></div>
              <div class="custom-search-skeleton-info">
                <div class="custom-search-skeleton-text-1"></div>
                <div class="custom-search-skeleton-text-2"></div>
                <div class="custom-search-skeleton-text-3"></div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;

    customContainer.innerHTML = `
      <div class="custom-search-grid">
        <div class="custom-search-top-banner"></div>
        <div class="custom-search-left-pane">${leftHTML}</div>
        <div class="custom-search-right-pane">${skeletonRight}</div>
      </div>
    `;

    updateFocusableItems();

    // Fetch products for the right pane asynchronously
    loadDefaultProducts();
  }

  // --- RECENTLY VIEWED PRODUCTS TRACKING ---
  function getRecentlyViewedProducts() {
    try {
      const stored = localStorage.getItem('custom_recently_viewed_v2');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  function trackCurrentProduct() {
    // Only track on product pages
    if (!window.location.pathname.startsWith('/products/')) return;
    const metaTag = document.querySelector('meta[property="og:title"]');
    const priceTag = document.querySelector('meta[property="product:price:amount"]');
    const imageTag = document.querySelector('meta[property="og:image"]');

    if (!metaTag) return;

    const product = {
      title: metaTag.content,
      url: window.location.pathname,
      // El meta OG viene con coma decimal ("1,67"); toCents lo parsea bien y
      // guardamos en euros con punto ("1.67"), consistente con suggest.json.
      price: priceTag ? (toCents(priceTag.content) / 100).toFixed(2) : '0',
      image: imageTag ? imageTag.content : '',
      compare_at_price: null
    };

    let viewed = getRecentlyViewedProducts();
    // Remove duplicate
    viewed = viewed.filter(p => p.url !== product.url);
    viewed.unshift(product);
    viewed = viewed.slice(0, 10);

    try {
      localStorage.setItem('custom_recently_viewed_v2', JSON.stringify(viewed));
    } catch (e) {
      // localStorage full, ignore
    }
  }

  // Track current page on load
  trackCurrentProduct();

  // --- LOAD DEFAULT PRODUCTS FOR RIGHT PANE ---
  async function loadDefaultProducts() {
    const rightPane = customContainer.querySelector('.custom-search-right-pane');
    if (!rightPane) return;

    let productsToShow = [];
    let sectionTitle = 'Más vendidos';

    // 1. Try recently viewed products first
    const recentlyViewed = getRecentlyViewedProducts();
    if (recentlyViewed.length > 0) {
      sectionTitle = 'Vistos recientemente';
      productsToShow = recentlyViewed.slice(0, 6);
    }

    // 2. Fetch best-selling products to fill remaining slots
    try {
      const res = await fetch('/collections/all/products.json?sort_by=best-selling&limit=6');
      if (res.ok) {
        const data = await res.json();
        if (data.products && data.products.length > 0) {
          const bestSelling = data.products.map(p => ({
            title: p.title,
            url: `/products/${p.handle}`,
            price: p.variants[0] ? p.variants[0].price : '0',
            compare_at_price: p.variants[0] ? p.variants[0].compare_at_price : null,
            image: p.images && p.images.length > 0 ? p.images[0].src : ''
          }));

          if (productsToShow.length > 0) {
            // Fill remaining slots with best sellers (avoid duplicates)
            const viewedUrls = new Set(productsToShow.map(p => p.url));
            const remaining = bestSelling.filter(p => !viewedUrls.has(p.url));
            productsToShow = productsToShow.concat(remaining).slice(0, 6);
          } else {
            productsToShow = bestSelling.slice(0, 6);
          }
        }
      }
    } catch (e) {
      console.error('Error fetching default products', e);
    }

    // 3. Render products if we have them
    if (productsToShow.length > 0) {
      rightPane.innerHTML = `
        <div class="custom-search-section">
          <h4 class="custom-search-title">${sectionTitle}</h4>
          <div class="custom-search-product-grid">
            ${productsToShow.map(p => {
              const onSale = p.compare_at_price && parseFloat(p.compare_at_price) > parseFloat(p.price);
              const formattedPrice = formatMoney(p.price);
              const formattedCompare = onSale ? formatMoney(p.compare_at_price) : '';
              return `
                <a href="${p.url}" class="custom-search-product-card">
                  ${onSale ? `<span class="custom-search-badge">Oferta</span>` : ''}
                  <div class="custom-search-img-wrapper">
                    <img class="custom-search-img" src="${p.image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-6_60x60.png'}" alt="${escapeHtml(p.title)}" loading="lazy">
                  </div>
                  <div class="custom-search-product-info">
                    <h5 class="custom-search-product-title">${escapeHtml(p.title)}</h5>
                    ${csProPriceRow(p, onSale, formattedPrice, formattedCompare)}
                  </div>
                </a>
              `;
            }).join('')}
          </div>
        </div>
      `;
    } else {
      rightPane.innerHTML = `
        <div class="custom-search-section">
          <h4 class="custom-search-title">Recomendado para ti</h4>
          <div style="font-size: 13px; color: var(--cs-text-muted); line-height: 1.5; padding: 10px 0;">
            Comienza a escribir para descubrir productos instantáneos, ofertas activas y categorías inteligentes.
          </div>
        </div>
      `;
    }

    updateFocusableItems();
  }

  // --- PERFORM AJAX PREDICTIVE SEARCH ---
  async function performSearch(query) {
    showDropdown();

    // 1. Show loading skeleton template in the right pane immediately
    const skeletonHTML = `
      <div class="custom-search-section">
        <h4 class="custom-search-title">${translations.products || 'Productos'}</h4>
        <div class="custom-search-product-grid">
          ${Array(6).fill(0).map(() => `
            <div class="custom-search-skeleton-card">
              <div class="custom-search-skeleton-img"></div>
              <div class="custom-search-skeleton-info">
                <div class="custom-search-skeleton-text-1"></div>
                <div class="custom-search-skeleton-text-2"></div>
                <div class="custom-search-skeleton-text-3"></div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;

    // 2. Perform spellcheck/typo correction
    const correctedQuery = suggestCorrections(query);
    let correctionBox = '';
    if (correctedQuery) {
      correctionBox = `
        <div class="custom-search-typo-correction">
          <p class="custom-search-typo-text">
            ${translations.did_you_mean} 
            <span class="custom-search-typo-link" data-term="${escapeHtml(correctedQuery)}">${escapeHtml(correctedQuery)}</span>?
          </p>
        </div>
      `;
    }

    // Render left pane typo-correction + suggestions, right pane skeleton loading
    customContainer.innerHTML = `
      <div class="custom-search-grid has-results">
        <div class="custom-search-top-banner">${correctionBox}</div>
        <div class="custom-search-left-pane">
          <div class="custom-search-section">
            <h4 class="custom-search-title">Búsqueda</h4>
            <div class="custom-search-pills-list">
              <button class="custom-search-pill-btn" type="button" data-term="${escapeHtml(query)}">
                Buscar "${escapeHtml(query)}"
              </button>
            </div>
          </div>
        </div>
        <div class="custom-search-right-pane">${skeletonHTML}</div>
      </div>
    `;

    // Nueva búsqueda: invalida respuestas anteriores y cancela la petición en vuelo.
    const token = ++latestSearchToken;
    if (currentSearchController) currentSearchController.abort();

    // Caché por consulta: si ya la tenemos, píntala al instante sin volver a pedir.
    const cacheKey = query.toLowerCase();
    if (searchCache.has(cacheKey)) {
      renderResults(query, correctedQuery, searchCache.get(cacheKey));
      return;
    }

    const controller = new AbortController();
    currentSearchController = controller;

    // Fetch resources from Predictive Search JSON API
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,page&resources[limit]=6`;
      const res = await fetch(url, { signal: controller.signal });
      if (token !== latestSearchToken) return; // respuesta obsoleta (el usuario ya escribió más)
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      if (token !== latestSearchToken) return; // respuesta obsoleta
      const results = (data.resources && data.resources.results) || {};
      if (searchCache.size > 50) searchCache.clear();
      searchCache.set(cacheKey, results);
      renderResults(query, correctedQuery, results);
    } catch (e) {
      if (e.name === 'AbortError' || token !== latestSearchToken) return; // cancelada u obsoleta
      console.error('Error fetching predictive search json', e);
      renderErrorState(query);
    }
  }

  // --- RENDER SEARCH RESULTS PANEL ---
  function renderResults(query, correctedQuery, results) {
    const { products = [], collections = [], articles = [], pages = [] } = results;
    let leftHTML = '';
    let rightHTML = '';

    // A. Left Pane layout (Typo, Queries Suggestions, Collections)
    let topHTML = '';
    if (correctedQuery) {
      topHTML = `
        <div class="custom-search-typo-correction">
          <p class="custom-search-typo-text">
            ${translations.did_you_mean} 
            <span class="custom-search-typo-link" data-term="${escapeHtml(correctedQuery)}">${escapeHtml(correctedQuery)}</span>?
          </p>
        </div>
      `;
    }

    // Suggestions / Queries
    leftHTML += `
      <div class="custom-search-section">
        <h4 class="custom-search-title">${translations.suggestions || 'Sugerencias'}</h4>
        <ul class="custom-search-linklist">
          <li class="custom-search-linklist-item">
            <a href="/search?q=${encodeURIComponent(query)}&type=product" class="custom-search-suggest-link" data-term="${escapeHtml(query)}">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <span>Buscar "<strong>${escapeHtml(query)}</strong>"</span>
            </a>
          </li>
          ${collections.slice(0, 3).map(col => `
            <li class="custom-search-linklist-item">
              <a href="${col.url}" class="custom-search-suggest-link" data-term="${escapeHtml(col.title)}">
                <svg viewBox="0 0 24 24"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                <span>${highlightMatch(col.title, query)}</span>
              </a>
            </li>
          `).join('')}
        </ul>
      </div>
    `;

    // Articles & Pages
    const pagesAndArticles = [...pages, ...articles].slice(0, 3);
    if (pagesAndArticles.length > 0) {
      leftHTML += `
        <div class="custom-search-section">
          <h4 class="custom-search-title">Contenido y Ayuda</h4>
          <ul class="custom-search-linklist">
            ${pagesAndArticles.map(item => `
              <li class="custom-search-linklist-item">
                <a href="${item.url}" class="custom-search-suggest-link">
                  <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                  <span>${escapeHtml(item.title)}</span>
                </a>
              </li>
            `).join('')}
          </ul>
        </div>
      `;
    }

    // B. Right Pane layout (Products Grid)
    if (products.length > 0) {
      rightHTML += `
        <div class="custom-search-section">
          <h4 class="custom-search-title">${translations.products || 'Productos'}</h4>
          <div class="custom-search-product-grid">
            ${products.map(p => {
              const onSale = p.compare_at_price && parseFloat(p.compare_at_price) > parseFloat(p.price);
              const formattedPrice = formatMoney(p.price);
              const formattedCompare = onSale ? formatMoney(p.compare_at_price) : '';

              return `
                <a href="${p.url}" class="custom-search-product-card">
                  ${onSale ? `<span class="custom-search-badge">Oferta</span>` : ''}
                  <div class="custom-search-img-wrapper">
                    <img class="custom-search-img" src="${p.image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-6_60x60.png'}" alt="${escapeHtml(p.title)}" loading="lazy">
                  </div>
                  <div class="custom-search-product-info">
                    <h5 class="custom-search-product-title">${highlightMatch(p.title, query)}</h5>
                    ${csProPriceRow(p, onSale, formattedPrice, formattedCompare)}
                  </div>
                </a>
              `;
            }).join('')}
          </div>
          
          <a class="custom-search-view-all-btn" href="/search?q=${encodeURIComponent(query)}&type=product">
            <span>${translations.view_all || 'Ver todos los resultados'}</span>
            <svg style="width:14px; height:14px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
          </a>
        </div>
      `;
    } else {
      rightHTML += `
        <div class="custom-search-empty-state">
          <h4 class="custom-search-empty-state-title">${translations.no_results || 'No hay resultados'}</h4>
          <p class="custom-search-empty-state-text">No pudimos encontrar nada para "${escapeHtml(query)}". Prueba con otros términos o revisa la ortografía.</p>
        </div>
      `;
    }

    customContainer.innerHTML = `
      <div class="custom-search-grid has-results">
        <div class="custom-search-top-banner">${topHTML}</div>
        <div class="custom-search-left-pane">${leftHTML}</div>
        <div class="custom-search-right-pane">${rightHTML}</div>
      </div>
    `;

    updateFocusableItems();
  }

  // --- RENDER ERROR STATE ---
  function renderErrorState(query) {
    customContainer.innerHTML = `
      <div class="custom-search-grid has-results">
        <div class="custom-search-top-banner"></div>
        <div class="custom-search-left-pane">
          <div class="custom-search-section">
            <h4 class="custom-search-title">Búsqueda</h4>
            <div class="custom-search-pills-list">
              <button class="custom-search-pill-btn" type="button" data-term="${escapeHtml(query)}">
                Buscar "${escapeHtml(query)}"
              </button>
            </div>
          </div>
        </div>
        <div class="custom-search-right-pane">
          <div class="custom-search-empty-state">
            <h4 class="custom-search-empty-state-title">Error de conexión</h4>
            <p class="custom-search-empty-state-text">Hubo un problema al buscar en vivo. Pulsa Enter para realizar la búsqueda normal.</p>
          </div>
        </div>
      </div>
    `;
    updateFocusableItems();
  }

  // --- KEYBOARD ACCESSIBILITY & NAVIGATION ---
  function handleKeyboardNav(e) {
    const key = e.key;

    if (key === 'Escape') {
      e.preventDefault();
      hideDropdown();
      searchInput.blur();
      return;
    }

    if (customContainer.style.display === 'none') {
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        showDropdown();
      }
      return;
    }

    if (key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(1);
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(-1);
    } else if (key === 'Enter') {
      // If a dropdown item is currently focused via keyboard, navigate to it instead of submitting
      if (activeFocusIndex >= 0 && currentFocusableItems[activeFocusIndex]) {
        e.preventDefault();
        currentFocusableItems[activeFocusIndex].click();
      }
    }
  }

  function updateFocusableItems() {
    currentFocusableItems = Array.from(customContainer.querySelectorAll(FOCUSABLE_SELECTOR));
    activeFocusIndex = -1;
  }

  function moveFocus(direction) {
    if (currentFocusableItems.length === 0) return;

    // Reset previous selection
    if (activeFocusIndex >= 0 && currentFocusableItems[activeFocusIndex]) {
      currentFocusableItems[activeFocusIndex].removeAttribute('aria-selected');
      currentFocusableItems[activeFocusIndex].classList.remove('is-active');
    }

    // Calculate next index
    activeFocusIndex = (activeFocusIndex + direction + currentFocusableItems.length) % currentFocusableItems.length;

    const currentItem = currentFocusableItems[activeFocusIndex];
    if (currentItem) {
      currentItem.setAttribute('aria-selected', 'true');
      currentItem.classList.add('is-active');
      currentItem.scrollIntoView({ block: 'nearest' });
      currentItem.focus();

      // Put focus back to input while preserving selection highlights (so user can continue typing)
      // This is a premium UX technique where typing is never interrupted by arrow navigation focus shifts
      searchInput.focus();
    }
  }

  function resetFocusState() {
    activeFocusIndex = -1;
    currentFocusableItems.forEach(item => {
      item.removeAttribute('aria-selected');
      item.classList.remove('is-active');
    });
  }

  // --- HELPERS ---

  // Escape HTML characters to prevent XSS in client-rendered templates
  function escapeHtml(string) {
    const matchHtmlRegExp = /["'&<>]/;
    const str = '' + string;
    const match = matchHtmlRegExp.exec(str);

    if (!match) return str;

    let escape;
    let html = '';
    let index = 0;
    let lastIndex = 0;

    for (index = match.index; index < str.length; index++) {
      switch (str.charCodeAt(index)) {
        case 34: // "
          escape = '&quot;';
          break;
        case 38: // &
          escape = '&amp;';
          break;
        case 39: // '
          escape = '&#39;';
          break;
        case 60: // <
          escape = '&lt;';
          break;
        case 62: // >
          escape = '&gt;';
          break;
        default:
          continue;
      }

      if (lastIndex !== index) {
        html += str.substring(lastIndex, index);
      }

      lastIndex = index + 1;
      html += escape;
    }

    return lastIndex !== index
      ? html + str.substring(lastIndex, index)
      : html;
  }

  // Highlight characters that match the search query
  function highlightMatch(title, query) {
    if (!title || !query) return escapeHtml(title);
    const escapedTitle = escapeHtml(title);
    const escapedQuery = escapeHtml(query).trim();

    if (escapedQuery === '') return escapedTitle;

    // Split query into words to match multi-word searches separately
    const words = escapedQuery.split(/\s+/).filter(w => w.length > 0);
    if (words.length === 0) return escapedTitle;

    // Create a regex matching any of the query words (case-insensitive)
    const pattern = `(${words.map(w => w.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|')})`;
    const regex = new RegExp(pattern, 'gi');

    return escapedTitle.replace(regex, '<span class="custom-search-highlight">$1</span>');
  }

  // Convierte un precio a CÉNTIMOS (entero), de forma robusta. Acepta:
  //  - number: se asume que YA está en céntimos (p.ej. neto calculado).
  //  - string en euros con separador decimal ',' o '.' y miles opcional:
  //    "1,67" → 167, "1.234,56" → 123456, "8.22" → 822, "8" → 800.
  // Esto evita el bug de parseFloat("1,67")=1 con el formato español (coma decimal).
  function toCents(v) {
    if (v == null || v === '') return 0;
    if (typeof v === 'number') return Math.round(v);
    let s = String(v).trim().replace(/[^\d.,-]/g, '');
    if (s === '' || s === '-') return 0;
    const dec = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
    let euros;
    if (dec === -1) {
      euros = parseFloat(s.replace(/[.,]/g, ''));
    } else {
      const intPart = s.slice(0, dec).replace(/[.,]/g, '');
      const fracPart = s.slice(dec + 1).replace(/[.,]/g, '');
      euros = parseFloat((intPart || '0') + '.' + (fracPart || '0'));
    }
    return isNaN(euros) ? 0 : Math.round(euros * 100);
  }

  // Currency / Price Formatting Helper. Formatea al formato de moneda de la tienda.
  function formatMoney(input) {
    const cents = toCents(input);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = window.theme.moneyFormat || "${{amount}}";

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    }

    const matches = formatString.match(placeholderRegex);
    if (!matches) return (cents / 100.0).toFixed(2);

    let value;
    switch (matches[1]) {
      case 'amount':
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_with_comma_separator':
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  // Precio dual para clientes profesionales (misma lógica que pro-price.liquid).
  // formatMoney espera céntimos (convierte strings quitando el punto), así que el
  // neto se calcula en céntimos: neto = round(bruto / (1 + IVA/100)).
  function csProPriceRow(p, onSale, formattedPrice, formattedCompare) {
    const pro = window.__proPricing;
    if (!(pro && pro.isPro && p.price != null)) {
      return `<div class="custom-search-price-row">
                      <span class="custom-search-price">${formattedPrice}</span>
                      ${onSale ? `<span class="custom-search-compare-price">${formattedCompare}</span>` : ''}
                    </div>`;
    }
    const grossCents = toCents(p.price);
    const vat = Number(pro.vatRate) || 0;
    // Fórmula idéntica a pro-price.liquid: neto = round(bruto_cent * 100 / (100 + IVA)).
    const netCents = Math.round(grossCents * 100 / (100 + vat));
    const netStr = formatMoney(netCents);
    const withoutLbl = escapeHtml(pro.labelWithout || 'sin IVA');
    const withLbl = escapeHtml(pro.labelWith || 'IVA incl.');
    const grossMarkup = `${formattedPrice}${onSale ? ` <s class="pro-price__compare">${formattedCompare}</s>` : ''}`;
    return `<div class="custom-search-price-row">
                      <span class="pro-price pro-price--net-main">
                        <span class="pro-price__main custom-search-price">${netStr} <span class="pro-price__tax-label">${withoutLbl}</span></span>
                        <span class="pro-price__sub">${grossMarkup} <span class="pro-price__tax-label">${withLbl}</span></span>
                      </span>
                    </div>`;
  }
})();
